<script setup lang="ts">
import { equals } from 'ramda';
import type { LocationQuery } from 'vue-router';

// icons
import { faListUl } from '@fortawesome/pro-solid-svg-icons';

const CiMap = defineAsyncComponent({
  loader: () => import('@/components/map/CiMap.vue'),
});

definePageMeta({
  layout: 'map',
});

/********************
 * COMPOSITIONS      *
 ********************/
const localePath = useLocalePath();
const route = useRoute();
const routeStore = useRouteStore();
const searchStore = useSearchStore();
const viewport = useViewport();
const { $gettext } = useGettext();
const { locale } = useI18n();
const loadingStore = useLoadingStore();
const { openModal, closeModal } = useCiModal();

/********************
 * REFS & VARS      *
 ********************/
const campsite = ref({});
const countrySlug = ref<string | null>(null);
const mapControls = ref<HTMLElement | null>(null);
const mapRef = ref<InstanceType<typeof CiMap> | null>(null);
const serpLink = ref<string | null>(null);

let enableChangeEventsTimeout: any = null;

const queryWithoutOnlyAvailabilities = computed(() => {
  const queryCopy = { ...route.query };
  delete queryCopy.only_availabilities;
  return queryCopy;
});

const isMobileView = computed(() => {
  return viewport.isLessThan('md');
});

const isAS = computed(() => {
  return !!(route.query.from || route.query.until);
});

const notOnlyAvailabilities = computed(() => {
  const oav = route.query.only_availabilities;
  return !oav || oav === 'false';
});

const notOnlyAvailabilitiesLabel = $gettext('Also show campsites without availability info');

const CiFilterModal = defineAsyncComponent({
  loader: () => import('@/components/filter/CiFilterPanel/CiFilterModal.vue'),
});

/********************
 * FUNCTIONS         *
 ********************/
function onMapChange(data: any) {
  if (!import.meta.client || !data.bbox) {
    return;
  }
  try {
    // Limit lng/lat params to 4 digits in URL param and resultset to max number
    const query = Object.assign({}, route.query, {
      area: data.bbox.join(','),
      zl: Math.round(data.zoom),
    });

    // Remove search string from query if map is changed
    const searchParams = ['q', 'city', 'country', 'region', 'federal_state', 'offset', 'campsite'];
    Object.keys(route.query)
      .filter(k => searchParams.includes(k))
      // eslint-disable-next-line @typescript-eslint/no-dynamic-delete
      .forEach(q => delete query[q]);

    for (const [key, value] of Object.entries(query)) {
      query[key] = value.toString();
    }

    // Push new url if no duplicate navigation occurs
    if (!equals(query, route.query)) {
      const q = route.query;

      // Detect initial automatic area parameter creation
      if (!q.country && !q.city && !q.federal_state && !q.region && !q.q && !q.area) {
        return navigateTo({ query }, { replace: true });
      } else {
        return navigateTo({ query });
      }
    }
  } catch (_e) {
    // _e
  }
}

function onCampsiteDeselected() {
  if (!import.meta.client) {
    return;
  }
  campsite.value = {};
}

async function onRouteChanged(newValue: LocationQuery, oldValue: LocationQuery | undefined) {
  if (!import.meta.client) {
    return;
  }

  const newVal = { ...newValue };
  const oldVal = { ...oldValue };
  delete newVal.campsite;
  delete oldVal.campsite;

  if (equals(newVal, oldVal)) {
    return;
  }

  await getCampsites(newValue);
}

let getCampsitesTimeout: any = null;

async function getCampsites(query: LocationQuery) {
  const s = query.federal_state || query.region || query.city || query.country || query.q || false;
  if (!query.area && !s) {
    return;
  }

  if (route.query.campsite) {
    mapRef.value?.closePreview();
    delete route.query.campsite;
  }

  loadingStore.setStatusLoading('mapCampsites');
  if (!mapRef.value || !mapRef.value.setMarkers) {
    if (getCampsitesTimeout) {
      clearTimeout(getCampsitesTimeout);
    }
    getCampsitesTimeout = setTimeout(() => {
      getCampsites(query);
    }, 10);
    return;
  }

  const queryCopy = { ...query };

  const isCPDeepLink = !!query.campsite;
  let zoomToMarker = false;
  const onlyAvailabilities = query.only_availabilities === undefined || query.only_availabilities === 'true';

  if (s) {
    try {
      try {
        clearTimeout(enableChangeEventsTimeout);
      } catch (_e) {
        // _e
      }
      enableChangeEventsTimeout = null;
      mapRef.value.disableChangeEvents();
    } catch (_e) {
      // _e
    }

    searchStore.setSearchParams({
      ...searchStore.searchParams,
      acco_type: query.acco_type || null,
      adults: query.adults ? parseInt(query.adults as string) : 2,
      children: query.children || null,
      flex: query.flex ? parseInt(query.flex as string) : searchStore.searchParams.flex,
      discounts: query.discounts || null,
      from: query.from || null,
      q: s,
      until: query.until || null,
      only_availabilities: onlyAvailabilities ? 'true' : 'false',
    });

    if (queryCopy.area) {
      // Remove bounding box param when regional search is used
      delete queryCopy.area;
      // Trigger zoom to marker bounds
    }
    if (queryCopy.zl) {
      delete queryCopy.zl;
    }
    zoomToMarker = true;
  } else {
    searchStore.setSearchParams({
      ...searchStore.searchParams,
      acco_type: query.acco_type || null,
      adults: query.adults ? parseInt(query.adults as string) : 2,
      children: query.children || null,
      flex: query.flex ? parseInt(query.flex as string) : searchStore.searchParams.flex,
      discounts: query.discounts || null,
      from: query.from || null,
      q: '',
      until: query.until || null,
      only_availabilities: onlyAvailabilities ? 'true' : 'false',
    });
  }

  queryCopy.lang = locale.value;
  delete queryCopy.offset;

  if (routeStore.searchState.isSet) {
    queryCopy.origin = routeStore.searchOrigin;
    // @ts-check
    queryCopy.foundLocation = routeStore.foundLocation;
    // @ts-check
    queryCopy.originalSearchTerm = routeStore.searchTerm;
    routeStore.setSearchState(null);
  }

  if (isCPDeepLink) {
    campsite.value = { slug: query.campsite };
    try {
      try {
        clearTimeout(enableChangeEventsTimeout);
      } catch (_e) {
        // _e
      }
      enableChangeEventsTimeout = null;
      mapRef.value.disableChangeEvents();
    } catch (_e) {
      // _e
    }
  }

  const response = await searchStore.getMapSearchResults({ lang: locale.value, query: queryCopy });
  let locationBounds = null;
  if (response && response.meta) {
    if (response.meta.location && response.meta.location.bounds) {
      locationBounds = response.meta.location.bounds;
    }

    if (response.meta.location && response.meta.location.country) {
      if (response.meta.location.country.slug) {
        countrySlug.value = response.meta.location.country.slug;
      } else if (response.meta.location.country.slugs) {
        countrySlug.value = response.meta.location.country.slugs[queryCopy.lang] || null;
      }
    } else {
      countrySlug.value = null;
    }

    if (response.meta.location && response.meta.location.name) {
      searchStore.setSearchParams({
        ...searchStore.searchParams,
        q: response.meta.location.name,
      });
    }
  }

  setSerpLink();

  if (isCPDeepLink) {
    zoomToMarker = false;
    locationBounds = null;
  }

  mapRef.value?.setMarkers(searchStore.mapResultItems, zoomToMarker, locationBounds);
  if (campsite.value?.slug) {
    mapRef.value?.openSelectedMarker(campsite.value);
  }

  loadingStore.setStatusSuccess('mapCampsites');

  if (enableChangeEventsTimeout === null) {
    enableChangeEventsTimeout = setTimeout(() => {
      enableChangeEventsTimeout = null;
      if (mapRef.value) {
        try {
          mapRef.value.enableChangeEvents();
        } catch (_e) {
          // _e
        }
      }
    }, 1500);
  }
}

function setSerpLink() {
  // copy query
  const query = { ...queryWithoutOnlyAvailabilities.value };

  let name;
  let params;

  delete query.campsite;

  if (route.query.region) {
    delete query.region;

    name = RBN_REGION_SEARCH;
    params = { regionSlug: route.query.region };
  } else if (route.query.city) {
    delete query.city;

    name = RBN_CITY_SEARCH;
    params = { citySlug: route.query.city };
  } else if (route.query.federal_state && countrySlug.value) {
    delete query.federal_state;
    delete query.country;

    name = RBN_FEDERAL_STATE_SEARCH;
    params = {
      countrySlug: countrySlug.value,
      federalStateSlug: route.query.federal_state,
    };
  } else if (route.query.federal_state && route.query.country) {
    delete query.federal_state;
    delete query.country;

    name = RBN_FEDERAL_STATE_SEARCH;
    params = {
      countrySlug: route.query.country,
      federalStateSlug: route.query.federal_state,
    };
  } else if (route.query.country) {
    delete query.country;

    name = RBN_COUNTRY_SEARCH;
    params = { countrySlug: route.query.country };
  } else {
    name = RBN_SEARCH;
    params = {};
  }

  serpLink.value = localePath({
    name,
    // @ts-check
    params,
    query,
  });
}

function showFilterModal() {
  if (!import.meta.client) {
    return;
  }

  openModal(
    {
      component: CiFilterModal,
      attrs: {
        name: 'CiFilterModal',
        onClose: () => {
          closeModal('CiFilterModal');
        },
      },
    },
    {
      clickToClose: true,
      contentTransition: 'translate-y-down',
      contentClass:
        'w-full max-w-[992px] lg:max-h-[900px] bg-white lg:rounded-lg p-0 flex items-center h-full overflow-hidden',
    },
  );
}

function toggleOnlyAvailabilities(checked: boolean) {
  const query = { ...route.query };
  if (query.only_availabilities === 'true' && checked) {
    query.only_availabilities = 'false';
  } else if (!query.only_availabilities || (query.only_availabilities === 'false' && !checked)) {
    query.only_availabilities = 'true';
  }
  searchStore.showOnlyCampsitesWithAvailabilitiesOnMap = !checked;
  if (!equals(query, route.query)) {
    return navigateTo({ query });
  }
}

/********************
 * WATCHER           *
 ********************/
watch(() => route.query, onRouteChanged, { immediate: true });
</script>

<template>
  <client-only>
    <div class="absolute left-0 top-0 size-full overflow-hidden">
      <CiMap
        ref="mapRef"
        :map-controls="mapControls"
        :pre-selected="campsite"
        :query="route.query"
        @map-change-bounds="onMapChange"
        @map-campsite-deselected="onCampsiteDeselected"
      />
    </div>

    <!-- controls -->
    <div
      ref="mapControls"
      class="relative mx-auto flex max-w-screen-xl flex-col items-center justify-between px-4 pt-4 lg:flex-row xl:px-0"
    >
      <CiSearch
        class="order-1 w-full min-w-0 lg:order-2"
        :is-mobile="isMobileView"
        collapsible
      />

      <!-- list -->
      <nuxt-link
        :to="serpLink"
        class="button button--primary absolute bottom-[-66px] left-4 order-2 mr-auto size-[50px] shadow-ci-sm md:bottom-[-56px] md:size-auto lg:relative lg:bottom-auto lg:left-auto lg:order-1 lg:mr-6 lg:mt-0"
      >
        <CiAwesomeIcon
          :icon="faListUl"
          class="icon-black"
          ratio="0.75"
        />
        <span class="ml-0 hidden truncate md:ml-2 md:block">{{ $gettext('List') }}</span>
      </nuxt-link>

      <!-- filter -->
      <button
        type="button"
        class="button button--primary button--filter absolute bottom-[-66px] right-4 order-2 size-[50px] shadow-ci-sm md:bottom-[-56px] md:size-auto lg:relative lg:bottom-auto lg:right-auto lg:ml-6 lg:mt-0"
        :class="{ 'button--filter--has-filter': searchStore.filterCount }"
        @click="showFilterModal"
      >
        <span class="flex flex-row text-nowrap">
          <span class="hidden md:block">{{ $gettext('Filter') }}</span>
          <span
            v-if="searchStore.filterCount"
            class="ml-1 hidden md:block"
          >({{ searchStore.filterCount }})</span>
        </span>
        <img
          src="~/assets/svg/icon-filter.svg"
          class="icon !ml-0 size-5 max-w-none md:!ml-4"
          alt="icon filter"
        />
      </button>
    </div>

    <div
      v-if="isAS"
      class="search-map__controls-footer pointer-events-none absolute bottom-[27px] mx-auto flex w-[calc(100%-44px)] flex-col items-center px-4 sm:w-full xl:px-0"
    >
      <div
        class="pointer-events-auto relative flex min-h-10 items-center rounded bg-white px-3 text-sm text-primary shadow-ci"
      >
        <InputCheckbox
          id="ov"
          class="!mb-0 py-2"
          :label="notOnlyAvailabilitiesLabel"
          :value="notOnlyAvailabilities"
          @input="toggleOnlyAvailabilities"
        />
      </div>
    </div>
  </client-only>
</template>

<style scoped>
:deep(.input-label--for-checkbox) {
  @apply m-0 select-none !font-medium leading-6;
}

:deep(.input-label--for-checkbox:before) {
  @apply border-2 border-primary bg-white;
}

:deep(.input-field--checkbox:checked + .input-label--for-checkbox:before) {
  @apply bg-primary;
}

:deep(.input-label--for-checkbox:after) {
  @apply border-white;
}
</style>
